import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes } from "react";
import { twMerge } from "tailwind-merge";

const badgeVariants = cva(
  [
    "inline-block",
    "text-neutral-black-95",
    "rounded-component-specific-badge",
    "font-body font-regular",
    "uppercase",
    "tabular-nums lining-nums",
    "leading-none",
  ],
  {
    variants: {
      size: {
        small:
          "py-grid-component-spacing-4xs px-grid-component-spacing-3xs text-xs",
        large:
          "py-grid-component-spacing-3xs px-grid-component-spacing-2xs text-sm",
      },
      color: {
        primaryLight: "bg-brand-blue-40",
        primaryDark: "bg-brand-blue-90 text-neutral-white-100",
        sand: "bg-brand-sand-20",
        gold: "bg-brand-yellow-60",
        sale: "bg-collection-coral-50",
        black: "bg-neutral-black-95 text-neutral-white-100",
        white: "bg-neutral-white-95",
        negative: "bg-status-red-40",
        positive: "bg-status-green-40",
      },
    },
    defaultVariants: {
      color: "primaryLight",
      size: "small",
    },
  },
);

type BadgeProps = VariantProps<typeof badgeVariants> &
  HTMLAttributes<HTMLSpanElement>;

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ size, color, className, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={twMerge(badgeVariants({ size, color }), className)}
        {...props}
      />
    );
  },
);

Badge.displayName = "Badge";
